import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = ["id", "name", "autocomplete", "disabled", "inputmode", "maxlength", "min", "max", "type", "pattern", "required", "spellcheck", "value"]
const _hoisted_3 = {
  key: 1,
  class: "pointer-events-none absolute left-4 top-[calc(50%_-_0.2857142rem)] leading-5"
}
const _hoisted_4 = ["for"]

import { computed, inject, onMounted, onUpdated, ref, toRef, watch } from "vue";
import ValidationText from "./SharedForm/ValidationText.vue";
import SharedButtonOnlyIcon from "./SharedButtonOnlyIcon.vue";
import HintText from "./SharedForm/HintText.vue";
import useInputValidation from "../composables/use-input-validation";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";


const __default__ = {
	model: {
		prop: "modelValue",
		event: "update:modelValue",
	},
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'SharedTextfield',
  props: {
    name: {},
    autocomplete: {},
    currency: {},
    disabled: { type: Boolean },
    displayName: {},
    hint: {},
    hintPermanent: { type: Boolean },
    inputmode: {},
    maxlength: {},
    min: {},
    max: {},
    modelValue: {},
    pattern: {},
    patternErrorMessage: {},
    forcedErrorMessage: {},
    placeholder: {},
    required: { type: Boolean },
    spellcheck: { type: Boolean },
    type: { default: "text" },
    value: { default: "" },
    iconName: {},
    requiredErrorMessage: {},
    dataTestName: { default: "" },
    validateOnBlur: { type: Boolean, default: false },
    validateOnChange: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "input", "change", "focus", "blur", "form-submit"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;



const localPattern = ref(props.pattern);
onMounted(() => {
	checkEmailPattern();
});

onUpdated(() => {
	checkEmailPattern();
});

const checkEmailPattern = () => {
	const useEmailPattern = props.type === "email" && !props.pattern;
	// hybris-RegEx für E-Mail: siehe DefaultConfigValues.java
	// ^[\\w.+\\-]+@(?!xn--)[\\w.\\-]+(\\.[A-Za-z]{2,})$
	// hier aber noch ergänzt mit "." nach dem @ (TUB-18168)
	const emailPattern = "^[\\w.+\\-]+@(?!xn--|\\.)[\\w.\\-]+(\\.[A-Za-z]{2,})$";
	localPattern.value = useEmailPattern ? emailPattern : props.pattern;
};



const inputRef = ref<HTMLInputElement | null>(null);
const hasModelValue = computed(() => typeof props.modelValue !== "undefined");

const inputValue = hasModelValue.value
	? computed({
			get() {
				return String(props.modelValue ?? "");
			},
			set(val: string) {
				emit("update:modelValue", val);
			},
	  })
	: ref(props.value);

const passwordHidden = ref(true);

const localType = ref(props.type);
const togglePassword = () => {
	passwordHidden.value = !passwordHidden.value;
	localType.value = passwordHidden.value ? "password" : "text";
};

const { isValidationVisible, errorMessage, handleInput, handleChange, handleBlur } = useInputValidation(
	inputRef,
	props.displayName || props.name || "Feld",
	{
		patternErrorMessage: props.patternErrorMessage,
	},
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

function handleInputLocally(event: Event) {
	const val = (event.target as HTMLInputElement).value;
	if (hasModelValue.value) {
		emit("update:modelValue", val);
	} else {
		inputValue.value = val;
	}

	handleInput(); // Validation
	emit("input", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
}

function handleChangeLocally(event: Event) {
	if (!props.validateOnBlur) handleChange(props.validateOnChange); // Validation
	emit("change", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
}

function handleBlurLocally(event: Event) {
	if (props.validateOnBlur) handleBlur(props.validateOnBlur); // Validation
	emit("blur", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
}

function handleFocusLocally(event: FocusEvent) {
	emit("focus", event as FocusEvent & { target: HTMLInputElement }, { errorMessage: errorMessage.value });
}

//#region formSubmit
const formRef = inject("formRef", ref<HTMLFormElement | null>(null));
if (formRef.value) {
	formRef?.value?.addEventListener("submit", handleSubmit);
} else {
	watch(formRef, () => {
		formRef?.value?.addEventListener("submit", handleSubmit);
	});
}

function handleSubmit(event: Event) {
	emit("form-submit", event as Event & { target: HTMLFormElement }, { errorMessage: errorMessage.value });
}
//#endregion formSubmit

// todo TUB-18950: remove public functions after AB-Test
// Public functions !!!! setFocus is only for TUB-18950 AB-Test !!!!
function setFocus() {
	inputRef.value?.focus();
}

__expose({
	setFocus,
	inputRef,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["tw flex flex-col text-base text-[var(--text-field-color)]", { 'focus-within:text-black': !_unref(isValidationVisible) }]),
    style: _normalizeStyle({
			'--text-field-color': _ctx.disabled
				? 'var(--color-gray-50)'
				: _unref(isValidationVisible)
				? 'var(--color-error)'
				: 'var(--color-gray-80)',
		})
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("input", _normalizeProps({
        id: _ctx.name,
        ref_key: "inputRef",
        ref: inputRef,
        name: _ctx.name,
        autocomplete: _ctx.autocomplete,
        disabled: _ctx.disabled,
        inputmode: _ctx.inputmode,
        maxlength: _ctx.maxlength,
        min: _ctx.min,
        max: _ctx.max,
        title: "",
        type: localType.value,
        pattern: localPattern.value,
        required: _ctx.required,
        spellcheck: props.type === 'email' ? 'false' : props.spellcheck,
        value: _unref(inputValue),
        class: ["peer h-15 w-full appearance-none rounded-t-lg border-b border-[var(--text-field-color)] bg-gray-20 px-4 pt-[0.8125em] text-black outline-none hover:bg-gray-40 focus-visible:border-b-0 focus-visible:bg-gray-40 focus-visible:pb-px focus-visible:shadow-[0_0_0_2px_var(--shadow-color)_inset] disabled:bg-gray-30 disabled:text-gray-50 [&::-webkit-credentials-auto-fill-button]:-mt-3 [&::-webkit-date-and-time-value]:text-left [&[type=password]]:pr-12", { 'cursor-not-allowed': _ctx.disabled, 'pr-10': _ctx.iconName, 'pl-8': _ctx.currency }],
        style: { '--shadow-color': _unref(isValidationVisible) ? 'var(--color-error)' : 'currentColor' },
        [_ctx.dataTestName || ""]: '',
        onFocus: handleFocusLocally,
        onChange: handleChangeLocally,
        onInput: handleInputLocally,
        onBlur: handleBlurLocally
      }), null, 16, _hoisted_2),
      (_ctx.iconName)
        ? (_openBlock(), _createBlock(SvgIcon, {
            key: 0,
            "icon-name": _ctx.iconName,
            class: "pointer-events-none absolute right-4 top-1/2 h-5 w-5 shrink-0 -translate-y-1/2 fill-current"
          }, null, 8, ["icon-name"]))
        : _createCommentVNode("", true),
      (_ctx.currency)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.currency), 1))
        : _createCommentVNode("", true),
      _createElementVNode("label", {
        for: _ctx.name,
        class: _normalizeClass(["pointer-events-none absolute left-4 top-0 flex h-15 origin-left select-none items-center transition-transform", [
					{
						'-translate-y-[0.9285714rem] scale-75': _unref(inputValue),
						'peer-hover:text-black': !_ctx.disabled && !_unref(isValidationVisible),
					},
					_ctx.type === 'date'
						? '-translate-y-[0.9285714rem] scale-75'
						: 'peer-focus-visible:-translate-y-[0.9285714rem] peer-focus-visible:scale-75',
				]])
      }, _toDisplayString(_ctx.placeholder), 11, _hoisted_4),
      (_ctx.type === 'password')
        ? (_openBlock(), _createBlock(SharedButtonOnlyIcon, {
            key: 2,
            class: "absolute right-0 top-1/2 -translate-y-1/2 text-black-primary",
            "icon-name": passwordHidden.value ? 'visibility' : 'visibility-off',
            "icon-size": "5",
            onClick: togglePassword
          }, null, 8, ["icon-name"]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.hint && (_ctx.hintPermanent || !_unref(isValidationVisible)))
      ? (_openBlock(), _createBlock(HintText, {
          key: 0,
          "hint-text": _ctx.hint,
          disabled: _ctx.disabled
        }, null, 8, ["hint-text", "disabled"]))
      : _createCommentVNode("", true),
    (_unref(isValidationVisible))
      ? (_openBlock(), _createBlock(ValidationText, {
          key: 1,
          "data-test-name": _ctx.dataTestName,
          "error-message": _unref(errorMessage)
        }, null, 8, ["data-test-name", "error-message"]))
      : _createCommentVNode("", true)
  ], 6))
}
}

})